import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/free-solid-svg-icons';
import type { FieldTypeDefinition, FieldRendererProps, FieldEditorProps } from '../registry';
import { containsHtml } from '../../utils/html';

const labelStyle = {
  fontSize: 'var(--kendo-font-size-lg)',
  fontWeight: 'var(--kendo-font-weight-bold)',
  padding: 'var(--kendo-spacing-2) 0',
} as const;

function LabelRenderer({ field }: FieldRendererProps) {
  // HTML only activates when a tag is actually present, so plain-text labels (the common
  // case) keep rendering exactly as before. The markup comes from the form author via the
  // builder (not from end-user-submitted answers), so this trust boundary is the same one
  // already used for HTML event titles in the sibling Fulcrum.Schedule.Embedded component.
  if (containsHtml(field.label)) {
    return <div style={labelStyle} dangerouslySetInnerHTML={{ __html: field.label }} />;
  }
  return <div style={labelStyle}>{field.label}</div>;
}

function LabelPropertyEditor({ field }: FieldEditorProps) {
  const html = containsHtml(field.label);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--kendo-spacing-2)' }}>
      <div style={{ fontSize: 'var(--kendo-font-size-xs)', color: 'var(--kendo-color-subtle)' }}>
        HTML in the Label field (General tab) renders live — e.g. <code>&lt;b&gt;Bold&lt;/b&gt;</code> or{' '}
        <code>&lt;span style="color:#ef4444"&gt;Red&lt;/span&gt;</code>. Plain text renders unchanged.
      </div>
      {html && (
        <div>
          <div
            style={{
              fontSize: 'var(--kendo-font-size-xs)',
              fontWeight: 'var(--kendo-font-weight-bold)',
              color: 'var(--kendo-color-subtle)',
              marginBottom: 'var(--kendo-spacing-1)',
            }}
          >
            Preview
          </div>
          <div
            style={{ border: '1px solid var(--kendo-color-border)', borderRadius: 'var(--kendo-border-radius-md)', padding: 'var(--kendo-spacing-2)' }}
            dangerouslySetInnerHTML={{ __html: field.label }}
          />
        </div>
      )}
    </div>
  );
}

export const definition: FieldTypeDefinition = {
  key: 'LABEL',
  label: 'Label / Header',
  category: 'layout',
  icon: <FontAwesomeIcon icon={faTag} fixedWidth />,
  isDisplayOnly: true,
  defaultProps: { label: 'Section header', isRequired: false },
  Renderer: LabelRenderer,
  PropertyEditor: LabelPropertyEditor,
};
